import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { getFileUrl } from '../../utils/fileUtils';
import AdminNavbar from '../../components/admin/AdminNavbar';
import './AdminCommon.css';

const AdminCasasEncuentro = () => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [telefono, setTelefono] = useState('');
  const [direccion, setDireccion] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [unaSolaFoto, setUnaSolaFoto] = useState(true);
  const [imagenFile, setImagenFile] = useState(null);
  const [nuevaImagenFile, setNuevaImagenFile] = useState(null);

  const { data: casas = [], isLoading } = useQuery({
    queryKey: ['casas-encuentro-admin'],
    queryFn: async () => {
      const res = await api.get('/casas-encuentro/admin/all');
      return res.data;
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, titulo, descripcion, telefono, direccion, video_url, una_sola_foto, imagen }) => {
      const formData = new FormData();
      if (titulo !== undefined) formData.append('titulo', titulo);
      if (descripcion !== undefined) formData.append('descripcion', descripcion);
      if (telefono !== undefined) formData.append('telefono', telefono);
      if (direccion !== undefined) formData.append('direccion', direccion);
      if (video_url !== undefined) formData.append('video_url', video_url);
      formData.append('una_sola_foto', una_sola_foto ? '1' : '0');
      if (imagen) formData.append('imagen', imagen);
      const res = await api.put(`/casas-encuentro/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['casas-encuentro-admin']);
      setEditingId(null);
      setImagenFile(null);
      alert('Casa actualizada correctamente.');
    },
    onError: (e) => {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  });

  const addImageMutation = useMutation({
    mutationFn: async ({ id, imagen }) => {
      const formData = new FormData();
      formData.append('imagen', imagen);
      const res = await api.post(`/casas-encuentro/${id}/imagenes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['casas-encuentro-admin']);
      setNuevaImagenFile(null);
      alert('Imagen añadida.');
    },
    onError: (e) => {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  });

  const deleteImageMutation = useMutation({
    mutationFn: async ({ casaId, imagenId }) => {
      await api.delete(`/casas-encuentro/${casaId}/imagenes/${imagenId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['casas-encuentro-admin']);
    },
    onError: (e) => {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  });

  const openEdit = (casa) => {
    setEditingId(casa.id);
    setTitulo(casa.titulo || '');
    setDescripcion(casa.descripcion || '');
    setTelefono(casa.telefono || '');
    setDireccion(casa.direccion || '');
    setVideoUrl(casa.video_url || '');
    setUnaSolaFoto(casa.una_sola_foto === 1);
    setImagenFile(null);
    setNuevaImagenFile(null);
  };

  const handleSave = (id) => {
    updateMutation.mutate({
      id,
      titulo,
      descripcion,
      telefono,
      direccion,
      video_url: videoUrl.trim() || '',
      una_sola_foto: unaSolaFoto,
      imagen: imagenFile || undefined
    });
  };

  const handleAddImage = (id) => {
    if (!nuevaImagenFile) { alert('Seleccione una imagen.'); return; }
    addImageMutation.mutate({ id, imagen: nuevaImagenFile });
  };

  if (isLoading) {
    return (
      <div className="admin-common">
        <AdminNavbar />
        <div className="container">Cargando casas de encuentro...</div>
      </div>
    );
  }

  return (
    <div className="admin-common">
      <AdminNavbar />
      <div className="container">
        <div className="admin-header">
          <Link to="/admin" className="btn btn-secondary">← Panel</Link>
          <h1>Casas de encuentro</h1>
          <p>Editar título, descripción, teléfono, dirección e imágenes de Casa Pastoral de San Juan y Casa El Capulí.</p>
        </div>

        <div className="casas-encuentro-admin">
          {casas.map((casa) => (
            <div key={casa.id} className="casa-encuentro-card">
              <h2>{casa.titulo}</h2>
              <p className="casa-slug">/{casa.slug}</p>

              {editingId === casa.id ? (
                <div className="casa-edit-form">
                  <label>Título</label>
                  <input type="text" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
                  <label>Descripción</label>
                  <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={4} />
                  <label>Teléfono</label>
                  <input type="text" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="Ej: +57 312 123 4567" />
                  <label>Dirección</label>
                  <input type="text" value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Dirección" />
                  <label>URL de video de YouTube</label>
                  <input type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." />
                  <label>
                    <input type="checkbox" checked={unaSolaFoto} onChange={(e) => setUnaSolaFoto(e.target.checked)} />
                    Una sola foto. Si no, galería (varias fotos).
                  </label>

                  {unaSolaFoto ? (
                    <>
                      <label>Imagen (reemplaza la actual)</label>
                      <input type="file" accept="image/*" onChange={(e) => setImagenFile(e.target.files?.[0] || null)} />
                    </>
                  ) : (
                    <>
                      <label>Imágenes actuales</label>
                      <div className="casa-imagenes-list">
                        {(casa.imagenes || []).map((img) => (
                          <div key={img.id} className="casa-imagen-item">
                            <img src={getFileUrl(img.imagen_url)} alt="" />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => deleteImageMutation.mutate({ casaId: casa.id, imagenId: img.id })}
                              disabled={deleteImageMutation.isLoading}
                            >
                              Eliminar
                            </button>
                          </div>
                        ))}
                      </div>
                      <label>Añadir otra imagen</label>
                      <div className="casa-add-imagen">
                        <input type="file" accept="image/*" onChange={(e) => setNuevaImagenFile(e.target.files?.[0] || null)} />
                        <button type="button" className="btn btn-primary" onClick={() => handleAddImage(casa.id)} disabled={!nuevaImagenFile || addImageMutation.isLoading}>
                          Subir imagen
                        </button>
                      </div>
                    </>
                  )}

                  <div className="form-actions">
                    <button type="button" className="btn btn-primary" onClick={() => handleSave(casa.id)} disabled={updateMutation.isLoading}>
                      Guardar
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => setEditingId(null)}>Cancelar</button>
                  </div>
                </div>
              ) : (
                <>
                  {casa.imagenes?.length > 0 && (
                    <div className="casa-preview-imagenes">
                      {casa.imagenes.slice(0, 3).map((img) => (
                        <img key={img.id} src={getFileUrl(img.imagen_url)} alt="" />
                      ))}
                      {casa.imagenes.length > 3 && <span>+{casa.imagenes.length - 3} más</span>}
                    </div>
                  )}
                  {(casa.telefono || casa.direccion) && (
                    <div className="casa-preview-meta">
                      {casa.telefono && <p><strong>Teléfono:</strong> {casa.telefono}</p>}
                      {casa.direccion && <p><strong>Dirección:</strong> {casa.direccion}</p>}
                    </div>
                  )}
                  <p className="casa-tipo">{casa.una_sola_foto === 1 ? 'Una sola foto' : 'Galería (varias fotos)'}</p>
                  <div className="casa-card-actions">
                    <button type="button" className="btn" onClick={() => openEdit(casa)}>Editar</button>
                    <Link to={`/casas-encuentro/${casa.slug}`} className="btn" target="_blank" rel="noopener noreferrer">Ver página</Link>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminCasasEncuentro;
